import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { isMobile } from "react-device-detect";
import ReCAPTCHA from "react-google-recaptcha";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  getRedirectResult,
  signInWithRedirect,
} from "firebase/auth";

import { SIGNUPMUTATION } from "../signup/queries";
import { ErrorType } from "../../../models/errors";
import {
  validateEmail,
  validatePassword,
} from "../../../middleware/common-functions";
import { useMutation } from "@apollo/client";
import { FormGroup } from "../../common/inputs";
import { SsoButton } from "../../common/buttons";
import { AnalyticsContext } from "../../../context/analytics-context";
import { useLocation } from "react-router-dom";
import { setAuthCookie } from "../../../middleware/cookie-manager";
import { LoginTokenType, SignupUser } from "../../../models/user";

const auth = getAuth();

type SignupFormProps = {
  button_txt?: string;
  promocode?: boolean | string;
  nextAction?: (data: any) => void;
};

const SignupForm: React.FC<SignupFormProps> = ({
  button_txt,
  promocode,
  nextAction,
}) => {
  const query = useQuery();
  const [error, setError] = useState<ErrorType | null>(null);
  const analyticsContext = useContext(AnalyticsContext);
  const location = useLocation();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [signupMutation, { data: signup_data, loading }] =
    useMutation(SIGNUPMUTATION);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    promocode:
      promocode === true || promocode === false ? "" : promocode?.toString(),
    how: "",
    langs: [] as string[],
  });
  useEffect(() => {
    const code = query.get("code");
    const lms_type = query.get("state") || "edlink";
    const error_url = query.get("error");
    const error_description = query.get("error_description");
    if (code) {
      edlinkSuignup(code, lms_type as LoginTokenType);
    } else if (error_url && error_description) {
      const set_error: ErrorType = {
        message: error_description,
      };
      setError(set_error);
    }
    ///login with Firebase on refresh
    getRedirectResult(auth)
      .then((result) => {
        if (!result) {
          return;
        }
        // This gives you a Google Access Token. You can use it to access the Google API.
        const credential = GoogleAuthProvider.credentialFromResult(result);
        if (!credential) {
          setError({
            message: "Something went wrong. Please try again.",
          });
          return;
        }
        // The signed-in user info.
        const new_user: SignupUser = {
          firebase_id: result.user.uid,
          email: result.user.email!,
          name: result.user.displayName!,
          firebase_token: credential.accessToken,
        };
        signupFunction(new_user);
      })
      .catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        setError({
          message: "Something went wrong. Please try again.",
          reason: `${errorMessage} | Code: ${errorCode}`,
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (promocode) {
      const set_form = { ...form, promocode: promocode.toString() };
      setForm(set_form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promocode]);
  useEffect(() => {
    if (signup_data && signup_data.teacherSignup) {
      const user = signup_data.teacherSignup;
      user.type = "teacher";
      user.is_teacher = true;
      if (user.admin === 0) {
        delete user.admin;
      }
      setAuthCookie(user);
      localStorage.removeItem("logoutType");
      ///save event to google analytics
      const event = {
        category: "User",
        action: "Account",
        label: `Create`,
        value: 1,
      };
      analyticsContext.event(event);
      if (nextAction) {
        nextAction(user);
        return;
      } else {
        window.location.href = "/thankyou";
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signup_data]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const type = e.target.id;
    const value = e.target.value;
    const set_form = { ...form, [type]: value };
    setForm(set_form);
  };

  const onChangeCaptcha = (value: any) => {
    console.log("signupForm.tsx:63 | value", value);
  };

  const handleGoogleSignup = async (
    e: React.ChangeEvent<HTMLButtonElement>,
  ) => {
    const provider = new GoogleAuthProvider();

    signInWithPopup(auth, provider)
      .then((result) => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        const credential = GoogleAuthProvider.credentialFromResult(result);
        if (!credential) {
          setError({
            message: "Something went wrong. Please try again.",
          });
          return;
        }

        // The signed-in user info.
        const new_user: SignupUser = {
          firebase_id: result.user.uid,
          email: result.user.email!,
          name: result.user.displayName!,
          firebase_token: credential.accessToken,
        };
        signupFunction(new_user);
      })
      .catch((error) => {
        // Handle Errors here.
        const errorCode = error.code;
        const errorMessage = error.message;
        setError({
          message: "Something went wrong. Please try again.",
          reason: `${errorMessage} | Code: ${errorCode}`,
        });
      });
  };

  const handleGoogleSignupMob = async (
    e: React.ChangeEvent<HTMLButtonElement>,
  ) => {
    const provider = new GoogleAuthProvider();

    signInWithRedirect(auth, provider);
  };

  const triggerCaptcha = async (): Promise<false | string> => {
    if (!recaptchaRef || !recaptchaRef.current) {
      return false;
    }
    try {
      const token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();
      return token || false;
    } catch (e) {
      console.log("signupForm.tsx:85 | error", e);
      return false;
    }
  };

  const edlinkSuignup = (code: string, lms_type: LoginTokenType) => {
    // The signed-in user info.
    const new_user: SignupUser = {
      email: "edlinkuser@mail.com",
      name: "Edlink User",
      token_code: code,
      token_type: lms_type,
    };
    signupFunction(new_user);
  };

  const signupFunction = async (new_user: SignupUser) => {
    const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const captcha_token =
      process.env.REACT_APP_PROD === "true"
        ? await triggerCaptcha()
        : "NOTOKEN";
    if (!captcha_token) {
      setError({
        message: "Something went wrong. Please try again.",
      });
      return;
    }

    try {
      const utm_source = getCookie("utm_source");
      const wholesaler_code = getCookie("wholesaler_code");
      await signupMutation({
        variables: {
          token: captcha_token,
          utm_source: utm_source,
          wholesaler: wholesaler_code || "",
          promocode: form.promocode || "",
          user: {
            email: new_user.email,
            firebase_id: new_user.firebase_id,
            firebase_token: new_user.firebase_token,
            token_code: new_user.token_code,
            token_type: new_user.token_type,
            password: new_user.password,
            name: new_user.name,
            timezone: time_zone,
          },
        },
      });
      try {
        //@ts-ignore next-line
        fbq("trackCustom", "Completed Registration");
      } catch (error) {
        console.log("signupForm.tsx:156 | error", error);
      }
      document.cookie =
        "utm_source=; wholesaler_code=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    } catch (error: unknown) {
      console.log("index.tsx:53 | error", error);
      // @ts-ignore next-line
      if (error.graphQLErrors[0].message) {
        setError({
          // @ts-ignore next-line
          message: error.graphQLErrors[0].message,
        });
      }
    }
  };
  const signupHandler = async () => {
    const email_val = form.email;
    const password_val = form.password;
    const user_name = form.name;
    if (user_name.length < 4) {
      setError({
        message: "Please add your Full Name (At least 4 characters) ",
      });
      return;
    }
    if (!validateEmail(email_val)) {
      setError({ message: "Please add a valid email address" });
      return;
    }
    if (!validatePassword(password_val)) {
      setError({
        message:
          password_val.length === 0
            ? "Password is required"
            : "Password must be at least 6 characters",
      });
      return;
    }
    setError(null);
    const new_user: SignupUser = {
      password: password_val,
      email: email_val!,
      name: user_name,
      token_type: "firebase",
    };
    signupFunction(new_user);
  };

  const handleEdlinkSignup = async (
    e: React.ChangeEvent<HTMLButtonElement>,
  ) => {
    const loginUrl = `https://ed.link/sso/login?client_id=9aabc0db-fdeb-43e7-aadd-028467a96ac9&redirect_uri=${process.env.REACT_APP_URL}${location.pathname}`;
    window.location.href = loginUrl;
  };

  const handleClasslinkSignup = async (
    e: React.ChangeEvent<HTMLButtonElement>,
  ) => {
    const clientId = process.env.REACT_APP_CLASS_LINK_CLIENT_ID;
    const redirectUri = `${process.env.REACT_APP_URL}${location.pathname}`;
    const loginUrl = `https://launchpad.classlink.com/oauth2/v2/auth?response_type=code&client_id=${clientId}&redirect_uri=${encodeURIComponent(
      redirectUri,
    )}&scope=profile&state=classlink`;
    window.location.href = loginUrl;
  };

  return (
    <>
      <p className="text-center text-muted small">
        Logging in with these buttons will work only after your LMS
        administrator has linked your account to Flangoo{" "}
        <a
          href="https://ed.link/integrate/9aabc0db-fdeb-43e7-aadd-028467a96ac9"
          className="text_underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          here
        </a>
        .
      </p>
      {promocode ? (
        <>
          <FormGroup
            handleChange={handleChange}
            label="Invitation Code"
            value={form.promocode || ""}
            type="text"
            id="promocode"
          />
          <hr />
        </>
      ) : null}
      <SsoButton
        txt="Sign up"
        fun={handleEdlinkSignup}
        type="Google Classroom"
      />
      <SsoButton txt="Sign up" fun={handleEdlinkSignup} type="Schoology" />
      <SsoButton txt="Sign up" fun={handleEdlinkSignup} type="Canvas" />
      <SsoButton txt="Sign up" fun={handleClasslinkSignup} type="ClassLink" />
      <SsoButton
        txt="Sign up"
        fun={isMobile ? handleGoogleSignupMob : handleGoogleSignup}
      />

      <p className="text-center my-2 text-muted">
        <small>Or</small>
      </p>
      <p className="text-muted small">* indicates a required field</p>
      <FormGroup
        handleChange={handleChange}
        label="Full Name"
        value={form.name}
        type="text"
        id="name"
        autoComplete="name"
        required
      />
      <FormGroup
        handleChange={handleChange}
        label="Email Address"
        value={form.email}
        type="email"
        id="email"
        autoComplete="email"
        required
      />
      <FormGroup
        handleChange={handleChange}
        label="Password"
        help={{ txt: "(Must be at least 6 characters)" }}
        value={form.password}
        type="password"
        id="password"
        autoComplete="new-password"
        required
      />

      <ReCAPTCHA
        // @ts-ignore next-line
        ref={recaptchaRef}
        size="invisible"
        sitekey={process.env.REACT_APP_RECAPTCHA_KEY!}
        onChange={onChangeCaptcha}
      />
      {error && !loading ? (
        <div className="alert alert-warning mt-2">
          <i
            className="fas fa-exclamation-triangle shake_anim"
            aria-hidden="true"
          ></i>{" "}
          {error.message}
        </div>
      ) : null}
      <button
        className="btn btn-primary btn-block py-3"
        id="signup_button"
        onClick={signupHandler}
        disabled={loading}
      >
        {loading ? (
          <span>
            <i
              className=" far fa-spinner-third fa-spin mr-2"
              aria-hidden="true"
            ></i>{" "}
            Creating Your Account{" "}
          </span>
        ) : button_txt ? (
          button_txt
        ) : (
          "Create Teacher Account"
        )}
      </button>
    </>
  );
};

function getCookie(cname: string) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) === " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }
  return undefined;
}

function useQuery() {
  const { search } = useLocation();

  return useMemo(() => new URLSearchParams(search), [search]);
}

export default SignupForm;
